// The posting-strategy card beside the interview plan (DC-121 "Posting strategy is a read-only sidebar card.";
// CR-014/015, owned by B2). BRD 3.2: "Preserve proposed vs current posting strategy and reason for change." Build Spec
// 3.1: "Given/When/Then: posting strategy retains original/current/reason." The plan reads B2's projection through
// intake.plan_open (`posting_strategy`, null until B2's read is on the trunk) and never writes it: B4 adds no posting
// tool. *Edit* is a link to B2's editor on the requisition, drawn only for the owning Recruiter or a covering grantee
// (`can.edit_strategy`); every other seat sees the values (DC-24: View → values, not controls).

import { Card, KeyValue, Paragraph, SURFACE_LABELS, TextLink, HISTORY_LABELS, VERSION_LABELS } from "@/mc";
import { PLAN_WORDS } from "./labels";

/** One posting strategy as B2's projection gives it: a word, or a version row carrying it. */
export type PostingValue = string | { strategy?: string | null; track?: string | null } | null | undefined;

export interface PostingStrategy {
  original?: PostingValue;
  current?: PostingValue;
  history?: readonly unknown[] | null;
  reason?: string | null;
}

export interface PostingCardProps {
  /** The requisition the card belongs to; *Edit* opens its record, where B2's editor lives. */
  rid: string;
  strategy: PostingStrategy | null | undefined;
  /** The owning Recruiter or a covering grantee (`can.edit_strategy`). */
  canEdit: boolean;
  className?: string | undefined;
}

/** The words of one strategy value (data from B2's projection); "—" when there is none. */
export function postingText(value: PostingValue): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value.trim() || "—";
  return value.strategy?.trim() || value.track?.trim() || "—";
}

export function PostingCard({ rid, strategy, canEdit, className }: PostingCardProps) {
  if (!strategy) return null;
  const edit = (
    <TextLink href={`#requisitions/${encodeURIComponent(rid)}`} can={canEdit} >
      {SURFACE_LABELS.edit}
    </TextLink>
  );
  return (
    <Card title={PLAN_WORDS.postingStrategy} actions={canEdit ? edit : undefined} className={className}>
      <KeyValue
        rows
        items={[
          [VERSION_LABELS.originalPostingType, postingText(strategy.original)],
          [VERSION_LABELS.currentPostingStrategy, postingText(strategy.current)],
          [HISTORY_LABELS.reason, strategy.reason?.trim() || "—"],
        ]}
      />
      <Paragraph>{PLAN_WORDS.postingRule}</Paragraph>
    </Card>
  );
}
